import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../../icons';
import { Alert, Btn, Card, SectionHeader, FormRow, Field, Tabs, Badge, Modal } from '../../components/ui';
import { usePermissions } from '../../hooks/usePermissions';
import { toastError, toastSuccess } from '../../utils/toast';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import {
  createBillingComponent,
  deleteBillingComponent,
  getBillingComponents,
  getBillingConfig,
  getBillingPreview,
  updateBillingComponent,
  updateBillingConfig,
} from '../../api/billing';
import { getSettings, updateSocietyInfo } from '../../api/settings';

const translations = { en, hi, gu };

const DEFAULT_SOCIETY = {
  name: '',
  type: 'CHS',
  reg_no: '',
  address: '',
  city: '',
  state: '',
  pin: '',
  founded: '',
  total_units: '',
  total_floors: '',
  email: '',
  phone: '',
  website: '',
  pan: '',
  gstin: '',
};

const DEFAULT_BILLING = {
  monthly_maintenance_amount: '3500',
  late_payment_fee: '500',
  grace_period_days: '10',
  bill_generation_day: '1',
  gst_enabled: false,
  gst_rate: '18',
};

const DEFAULT_COMM = {
  email_from: 'admin@sunriseheights.in',
  sms_provider: 'Twilio',
  whatsapp_enabled: false,
  razorpay_enabled: true,
  smtp_host: 'smtp.gmail.com',
  smtp_port: '587',
};

const BILLING_COMPONENT_TEMPLATE = { id: null, name: '', amount: '', is_active: true };

export default function SocietySettingsScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.settings?.[key] || translations.en?.settings?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const { hasPermission, hasRole } = usePermissions();
  const canEditBilling = hasPermission('society_settings.update') || hasRole(['super_admin', 'admin']);

  const [tab, setTab] = useState(0);
  const [saved, setSaved] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [savingSociety, setSavingSociety] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [componentModalOpen, setComponentModalOpen] = useState(false);
  const [componentSaving, setComponentSaving] = useState(false);
  const [componentDeletingId, setComponentDeletingId] = useState(null);
  const [editingComponent, setEditingComponent] = useState(null);

  const [society, setSociety] = useState(DEFAULT_SOCIETY);
  const [billing, setBilling] = useState(DEFAULT_BILLING);
  const [components, setComponents] = useState([]);
  const [comm, setComm] = useState(DEFAULT_COMM);
  const [componentForm, setComponentForm] = useState(BILLING_COMPONENT_TEMPLATE);

  const ss = key => e => setSociety(prev => ({ ...prev, [key]: e.target.value }));
  const sb = key => e => setBilling(prev => ({ ...prev, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const sc = key => e => setComm(prev => ({ ...prev, [key]: e.target.value }));
  const sct = key => setComm(prev => ({ ...prev, [key]: !prev[key] }));

  const parseNumber = (value) => Number(value || 0);
  const preview = useMemo(() => {
    const base = parseNumber(billing.monthly_maintenance_amount);
    const late = parseNumber(billing.late_payment_fee);
    const gst = billing.gst_enabled ? Math.round(base * parseNumber(billing.gst_rate) / 100) : 0;
    return {
      base_amount: base,
      late_fee: late,
      gst_amount: gst,
      total_amount: base + late + gst,
    };
  }, [billing]);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const [configRes, componentsRes, settingsRes] = await Promise.all([
        getBillingConfig(),
        getBillingComponents(),
        getSettings(),
      ]);

      const config = configRes?.data?.data || configRes?.data || {};
      const nextBilling = {
        monthly_maintenance_amount: String(config.monthly_maintenance_amount ?? DEFAULT_BILLING.monthly_maintenance_amount),
        late_payment_fee: String(config.late_payment_fee ?? DEFAULT_BILLING.late_payment_fee),
        grace_period_days: String(config.grace_period_days ?? DEFAULT_BILLING.grace_period_days),
        bill_generation_day: String(config.bill_generation_day ?? DEFAULT_BILLING.bill_generation_day),
        gst_enabled: Boolean(config.gst_enabled),
        gst_rate: String(config.gst_rate ?? DEFAULT_BILLING.gst_rate),
      };
      setBilling(nextBilling);
      setComponents(componentsRes?.data?.data || componentsRes?.data || []);

      const societyData = settingsRes?.data?.data?.society || settingsRes?.data?.society || {};
      const settingsData = settingsRes?.data?.data?.settings || settingsRes?.data?.settings || {};
      const profile = settingsData.profile || {};

      setSociety({
        name: societyData.name || '',
        type: societyData.type || 'CHS',
        reg_no: societyData.registration_no || '',
        address: societyData.address || '',
        city: societyData.city || '',
        state: profile.state || '',
        pin: profile.pin || '',
        founded: profile.founded || '',
        total_units: societyData.total_units ? String(societyData.total_units) : '',
        total_floors: profile.total_floors ? String(profile.total_floors) : '',
        email: profile.email || '',
        phone: profile.phone || '',
        website: profile.website || '',
        pan: profile.pan || '',
        gstin: profile.gstin || '',
      });

    } catch (err) {
      toastError(err?.message || t("toast_load_billing_failed"));
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveSociety = async () => {
    if (!canEditBilling) return; // reusing edit permission
    setSavingSociety(true);
    try {
      await updateSocietyInfo({
        name: society.name,
        type: society.type,
        registration_no: society.reg_no,
        address: society.address,
        city: society.city,
        total_units: parseInt(society.total_units, 10) || 0,
        settings: {
          profile: {
            state: society.state,
            pin: society.pin,
            founded: society.founded,
            total_floors: parseInt(society.total_floors, 10) || 0,
            email: society.email,
            phone: society.phone,
            website: society.website,
            pan: society.pan,
            gstin: society.gstin,
          }
        }
      });
      toastSuccess("Society details updated successfully");
      setSaved(true);
    } catch (err) {
      toastError(err?.message || "Failed to update society details");
    } finally {
      setSavingSociety(false);
    }
  };

  const saveBilling = async () => {
    if (!canEditBilling) return;

    setSavingBilling(true);
    try {
      await updateBillingConfig({
        monthly_maintenance_amount: parseNumber(billing.monthly_maintenance_amount),
        late_payment_fee: parseNumber(billing.late_payment_fee),
        grace_period_days: parseInt(billing.grace_period_days, 10),
        bill_generation_day: parseInt(billing.bill_generation_day, 10),
        gst_enabled: Boolean(billing.gst_enabled),
        gst_rate: parseNumber(billing.gst_rate),
      });
      toastSuccess(t("toast_billing_updated"));
      setSaved(true);
      await loadData();
    } catch (err) {
      toastError(err?.message || t("toast_save_billing_failed"));
    } finally {
      setSavingBilling(false);
    }
  };

  const openComponentModal = (component = null) => {
    setEditingComponent(component);
    setComponentForm(component ? { ...component } : BILLING_COMPONENT_TEMPLATE);
    setComponentModalOpen(true);
  };

  const saveComponent = async () => {
    if (!canEditBilling) return;
    setComponentSaving(true);
    try {
      const payload = {
        name: componentForm.name,
        amount: parseNumber(componentForm.amount),
        is_active: Boolean(componentForm.is_active),
      };
      if (editingComponent?.id) {
        await updateBillingComponent(editingComponent.id, payload);
        toastSuccess(t("toast_component_updated"));
      } else {
        await createBillingComponent(payload);
        toastSuccess(t("toast_component_added"));
      }
      setComponentModalOpen(false);
      setEditingComponent(null);
      setComponentForm(BILLING_COMPONENT_TEMPLATE);
      await loadData();
    } catch (err) {
      toastError(err?.message || t("toast_save_component_failed"));
    } finally {
      setComponentSaving(false);
    }
  };

  const removeComponent = async (component) => {
    if (!canEditBilling) return;
    if (!window.confirm(t("msg_confirm_delete_component", { name: component.name }))) return;
    setComponentDeletingId(component.id);
    try {
      await deleteBillingComponent(component.id);
      toastSuccess(t("toast_component_deleted"));
      await loadData();
    } catch (err) {
      toastError(err?.message || t("toast_delete_component_failed"));
    } finally {
      setComponentDeletingId(null);
    }
  };

  useEffect(() => {
    Promise.resolve(getBillingPreview({
      monthly_maintenance_amount: billing.monthly_maintenance_amount,
      late_payment_fee: billing.late_payment_fee,
      gst_enabled: billing.gst_enabled,
      gst_rate: billing.gst_rate,
    })).catch(() => {
      // Preview is also computed locally; ignore server preview failures here.
    });
  }, [billing.monthly_maintenance_amount, billing.late_payment_fee, billing.gst_enabled, billing.gst_rate]);

  const Toggle = ({ on, onToggle, label, desc }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
      <div className="flex-1 pr-4">
        <div className="text-sm font-semibold text-ink dark:text-slate-100">{label}</div>
        {desc && <div className="text-xs text-muted dark:text-slate-400 mt-0.5">{desc}</div>}
      </div>
      <button
        onClick={(e) => {
          if (window.navigator && window.navigator.vibrate) {
            window.navigator.vibrate(50);
          }
          onToggle(e);
        }}
        disabled={!canEditBilling}
        className="flex-shrink-0 border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          width: 40,
          height: 22,
          borderRadius: 11,
          background: on ? '#4f46e5' : '#e2e8f0',
          transition: 'background .2s',
          position: 'relative',
        }}
      >
        <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: on ? 21 : 3, transition: 'left .2s ease', boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }} />
      </button>
    </div>
  );

  return (
    <div className="fade-up space-y-4 max-w-4xl">
      {saved && <Alert type="success" onClose={() => setSaved(false)}>Society settings saved!</Alert>}

      <div className="flex items-center justify-between gap-3">
        <Tabs tabs={[t("tab_general"), t("tab_billing_config")]} active={tab} onChange={setTab} />
        {tab === 0 && (
          <Btn sm variant="primary" icon="check" loading={savingSociety || loadingData} onClick={saveSociety} disabled={!canEditBilling}>Save Details</Btn>
        )}
        {tab === 1 && (
          <Btn sm variant="primary" icon="check" loading={savingBilling || loadingData} onClick={saveBilling} disabled={!canEditBilling}>{t("btn_save_billing")}</Btn>
        )}
      </div>

      {tab === 0 && (
        <Card>
          <SectionHeader title={t("title_society_info")} subtitle={loadingData ? "Loading society information..." : t("subtitle_society_info")} />
          <FormRow cols={2}><Field label={t("label_society_name")} value={society.name} onChange={ss('name')} placeholder="e.g. Sunrise Heights CHS" required /><Field label={t("label_society_type")} type="select" value={society.type} onChange={ss('type')} options={['CHS', 'CGHS', 'AOA', 'HOA', 'Condominium', 'Township']} /></FormRow>
          <FormRow cols={2}><Field label={t("label_reg_no")} value={society.reg_no} onChange={ss('reg_no')} placeholder="e.g. CHS/MH/2014/12345" /><Field label={t("label_year_founded")} value={society.founded} onChange={ss('founded')} placeholder="e.g. 2014" /></FormRow>
          <FormRow><Field label={t("label_full_address")} value={society.address} onChange={ss('address')} placeholder="e.g. Plot 7, New Link Road, Andheri West, Mumbai - 400053" /></FormRow>
          <FormRow cols={3}><Field label={t("label_city")} value={society.city} onChange={ss('city')} placeholder="e.g. Mumbai" /><Field label={t("label_state")} value={society.state} onChange={ss('state')} placeholder="e.g. Gujarat" /><Field label={t("label_pin_code")} value={society.pin} onChange={ss('pin')} placeholder="e.g. 400053" /></FormRow>
          <FormRow cols={2}><Field label={t("label_total_units")} type="number" value={society.total_units} onChange={ss('total_units')} placeholder="e.g. 120" /><Field label={t("label_total_floors")} type="number" value={society.total_floors} onChange={ss('total_floors')} placeholder="e.g. 12" /></FormRow>
          <SectionHeader title={t("title_contact_tax")} border />
          <FormRow cols={2}><Field label={t("label_official_email")} type="email" value={society.email} onChange={ss('email')} placeholder="e.g. admin@sunriseheights.in" /><Field label={t("label_phone")} value={society.phone} onChange={ss('phone')} placeholder="e.g. 022-4567-8901" /></FormRow>
          <FormRow cols={2}><Field label={t("label_pan")} value={society.pan} onChange={ss('pan')} placeholder="e.g. AABCS1234D" /><Field label={t("label_gstin")} value={society.gstin} onChange={ss('gstin')} placeholder="e.g. 27AABCS1234D1Z5" /></FormRow>
        </Card>
      )}

      {tab === 1 && (
        <div className="space-y-4">
          <Card>
            <SectionHeader title={t("title_billing_config")} subtitle={loadingData ? t("subtitle_loading_billing") : t("subtitle_billing_config")} />
            <FormRow cols={2}>
              <Field label={t("label_monthly_maintenance")} type="number" value={billing.monthly_maintenance_amount} onChange={sb('monthly_maintenance_amount')} note={t("note_monthly_maintenance")} />
              <Field label={t("label_late_fee")} type="number" value={billing.late_payment_fee} onChange={sb('late_payment_fee')} note={t("note_late_fee")} />
            </FormRow>
            <FormRow cols={2}>
              <Field label={t("label_grace_period")} type="number" value={billing.grace_period_days} onChange={sb('grace_period_days')} note={t("note_grace_period")} />
              <Field label={t("label_bill_generation_day")} type="number" value={billing.bill_generation_day} onChange={sb('bill_generation_day')} note={t("note_bill_generation_day")} />
            </FormRow>
            <Toggle on={billing.gst_enabled} onToggle={() => setBilling(prev => ({ ...prev, gst_enabled: !prev.gst_enabled }))} label={t("label_gst_applicable")} desc={t("desc_gst_applicable")} />
            {billing.gst_enabled && <div className="pt-3"><Field label={t("label_gst_rate")} type="select" value={billing.gst_rate} onChange={sb('gst_rate')} options={['0', '5', '12', '18']} /></div>}

            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="text-xs font-bold text-blue-700 dark:text-blue-200">{t("title_billing_preview")}</div>
                <Badge color="blue" dot>{t("badge_live")}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-center text-xs">
                {[
                  [t("preview_base_maintenance"), `₹${preview.base_amount.toLocaleString()}`],
                  [t("preview_late_fee"), `₹${preview.late_fee.toLocaleString()}`],
                  [t("preview_gst_amount"), billing.gst_enabled ? `₹${preview.gst_amount.toLocaleString()}` : 'N/A'],
                  [t("preview_total_payable"), `₹${preview.total_amount.toLocaleString()}`],
                ].map(([label, value]) => (
                  <div key={label} className="bg-white dark:bg-slate-900 rounded-lg p-3 border border-blue-100 dark:border-slate-700">
                    <div className="font-black text-ink dark:text-slate-100 text-sm">{value}</div>
                    <div className="text-muted dark:text-slate-400 text-sm mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-start justify-between gap-3">
              <SectionHeader title={t("title_bill_components")} subtitle={t("subtitle_bill_components")} />
              <Btn xs icon="plus" onClick={() => openComponentModal()} disabled={!canEditBilling}>{t("btn_add_component")}</Btn>
            </div>
            <div className="space-y-3 mt-2">
              {components.length ? components.map((component) => (
                <div key={component.id} className="flex flex-col gap-3 py-3 border-b border-slate-100 dark:border-slate-700 last:border-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                      <Icon name="billing" size={15} className="text-muted" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-ink dark:text-slate-100">{component.name}</div>
                      <div className="text-xs text-muted dark:text-slate-400">₹{Number(component.amount).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge color={component.is_active ? 'green' : 'gray'} dot>{component.is_active ? t("badge_active") : t("badge_inactive")}</Badge>
                    <Btn xs variant="outline_primary" onClick={() => openComponentModal(component)} disabled={!canEditBilling}>{t("btn_edit")}</Btn>
                    <Btn xs variant="danger" loading={componentDeletingId === component.id} onClick={() => removeComponent(component)} disabled={!canEditBilling}>{t("btn_delete")}</Btn>
                  </div>
                </div>
              )) : (
                <Alert type="info">{t("msg_no_components")}</Alert>
              )}
            </div>
          </Card>
        </div>
      )}

      {tab === 2 && (
        <div className="space-y-4">
          <Card>
            <SectionHeader title="Email Configuration" subtitle="SMTP settings for outbound system emails" />
            <FormRow cols={2}><Field label="From Email" type="email" value={comm.email_from} onChange={sc('email_from')} /><Field label="SMTP Host" value={comm.smtp_host} onChange={sc('smtp_host')} /></FormRow>
            <FormRow cols={2}><Field label="SMTP Port" value={comm.smtp_port} onChange={sc('smtp_port')} /><Field label="SMS Provider" type="select" value={comm.sms_provider} onChange={sc('sms_provider')} options={['Twilio', 'MSG91', 'TextLocal', 'AWS SNS']} /></FormRow>
          </Card>
          <Card>
            <SectionHeader title="Channel Status" />
            <Toggle on={comm.whatsapp_enabled} onToggle={() => sct('whatsapp_enabled')} label="WhatsApp Business API" desc="Send resident notifications via WhatsApp" />
            <Toggle on={comm.razorpay_enabled} onToggle={() => sct('razorpay_enabled')} label="Razorpay Payment Gateway" desc="Accept online maintenance payments" />
          </Card>
        </div>
      )}

      {tab === 3 && (
        <Card>
          <SectionHeader title="Third-Party Integrations" subtitle="Connected services and API configuration" />
          {[
            { name: 'Razorpay', desc: 'Payment gateway for online collections', status: 'Connected', color: 'green', icon: 'billing' },
            { name: 'Twilio SMS', desc: 'SMS and OTP delivery service', status: 'Connected', color: 'green', icon: 'phone_call' },
            { name: 'Google Workspace', desc: 'SSO and Google Drive integration', status: 'Not configured', color: 'gray', icon: 'globe' },
            { name: 'WhatsApp Business', desc: 'Resident communication via WhatsApp', status: 'Not configured', color: 'gray', icon: 'wifi' },
            { name: 'Tally / QuickBooks', desc: 'Accounting software data sync', status: 'Not configured', color: 'gray', icon: 'money' },
          ].map((int, i) => (
            <div key={i} className="flex items-center gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
              <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 border border-border dark:border-slate-700 flex items-center justify-center">
                <Icon name={int.icon} size={17} className="text-muted dark:text-slate-400" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-ink dark:text-slate-100">{int.name}</div>
                <div className="text-xs text-muted dark:text-slate-400">{int.desc}</div>
              </div>
              <Badge color={int.color} dot>{int.status}</Badge>
              <Btn xs>{int.status === 'Connected' ? 'Configure' : 'Connect'}</Btn>
            </div>
          ))}
        </Card>
      )}

      {componentModalOpen && (
        <Modal title={editingComponent ? t("modal_edit_component") : t("modal_add_component")} onClose={() => setComponentModalOpen(false)}>
          <div className="space-y-4">
            <Field label={t("label_component_name")} value={componentForm.name} onChange={e => setComponentForm(prev => ({ ...prev, name: e.target.value }))} placeholder={t("placeholder_component_name")} />
            <Field label={t("label_amount")} type="number" value={componentForm.amount} onChange={e => setComponentForm(prev => ({ ...prev, amount: e.target.value }))} />
            <Toggle
              on={Boolean(componentForm.is_active)}
              onToggle={() => setComponentForm(prev => ({ ...prev, is_active: !prev.is_active }))}
              label={t("label_active")}
              desc={t("desc_active_component")}
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setComponentModalOpen(false)}>{t("btn_cancel")}</Btn>
              <Btn variant="primary" loading={componentSaving} onClick={saveComponent}>{editingComponent ? t('btn_update_component') : t('btn_add_component_modal')} {t("title_bill_components").split(" ")[1]}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
